import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { PageHeader } from '@/components/ui-kit/PageBits';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Flame, Star, Trophy, User as UserIcon, KeyRound, Save } from 'lucide-react';

export default function StudentProfile() {
  const { user, refresh } = useAuth();
  const [me, setMe] = useState(null);
  const [history, setHistory] = useState([]);
  const [achs, setAchs] = useState([]);
  const [academicProgress, setAcademicProgress] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });
  const [savingPwd, setSavingPwd] = useState(false);

  useEffect(() => {
    api.get('/auth/me').then(r => {
      setMe(r.data);
      setFirstName(r.data.user?.first_name || '');
      setLastName(r.data.user?.last_name || '');
    });
    api.get('/points/history').then(r => setHistory(r.data)).catch(()=>{});
    api.get('/achievements/mine').then(r => setAchs(r.data)).catch(()=>{});
    api.get('/students/me/topic-progress').then(r => setAcademicProgress(r.data)).catch(()=>{});
  }, []);

  const s = me?.student;

  const saveName = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('Nombre y apellido son obligatorios'); return;
    }
    setSavingName(true);
    try {
      await api.patch('/auth/me', { first_name: firstName.trim(), last_name: lastName.trim() });
      await refresh();
      toast.success('Datos actualizados');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSavingName(false); }
  };

  const savePwd = async () => {
    if (!pwd.current || !pwd.next) { toast.error('Rellena todos los campos'); return; }
    if (pwd.next.length < 6) { toast.error('Mínimo 6 caracteres'); return; }
    if (pwd.next !== pwd.confirm) { toast.error('Las contraseñas no coinciden'); return; }
    setSavingPwd(true);
    try {
      const { data } = await api.post('/auth/change-password', {
        current_password: pwd.current, new_password: pwd.next
      });
      if (data?.token) localStorage.setItem('c4k_token', data.token);
      toast.success('Contraseña actualizada. Otros dispositivos deberán volver a iniciar sesión.');
      setPwd({ current: '', next: '', confirm: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally { setSavingPwd(false); }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <PageHeader title="Mi perfil" subtitle="Tu progreso, cuenta y seguridad" testId="profile-header"/>

      <div className="rounded-3xl bg-white border border-slate-200 p-8 mb-6 flex items-center gap-6">
        <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-amber-400 to-orange-500 grid place-items-center text-white text-3xl font-display font-extrabold">
          {user?.first_name?.[0]}
        </div>
        <div className="flex-1">
          <div className="font-display text-3xl font-extrabold" data-testid="profile-display-name">{user?.first_name} {user?.last_name}</div>
          <div className="text-sm text-slate-500">{user?.email}</div>
          <div className="mt-3 flex flex-wrap gap-4 text-sm">
            <span className="inline-flex items-center gap-1"><Star className="w-4 h-4 text-amber-500"/> <b>{s?.points || 0}</b> pts</span>
            <span className="inline-flex items-center gap-1"><Flame className="w-4 h-4 text-orange-500"/> <b>{s?.streak_days || 0}</b> días</span>
            <span className="inline-flex items-center gap-1"><Trophy className="w-4 h-4 text-amber-600"/> <b>{achs.length}</b> logros</span>
            <span className="font-mono">Nivel {s?.gamification_level || 1} · Rating {s?.chess_rating || 800}</span>
          </div>
        </div>
      </div>

      {academicProgress?.enrollments?.length > 0 && (() => {
        const currentEnrollment = [...academicProgress.enrollments]
          .filter(item => item.enrollment?.status === 'in_progress')
          .sort((a, b) => {
            const dateA = new Date(a.enrollment?.started_at || 0).getTime();
            const dateB = new Date(b.enrollment?.started_at || 0).getTime();
            return dateB - dateA;
          })[0] || academicProgress.enrollments[academicProgress.enrollments.length - 1];

        const summary = currentEnrollment?.summary || {};
        const moduleSlot = currentEnrollment?.enrollment?.module_slot;

        const moduleLevel = moduleSlot
          ? moduleSlot <= 4
            ? 'Básico'
            : moduleSlot <= 8
              ? 'Intermedio'
              : 'Avanzado'
          : '—';

        return (
          <div className="rounded-3xl bg-white border border-slate-200 p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-blue-600">
                  Trayectoria académica
                </div>
                <h2 className="font-display font-extrabold text-2xl text-slate-900 mt-1">
                  Módulo {moduleSlot}
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Nivel {moduleLevel} · {currentEnrollment?.enrollment?.status === 'in_progress' ? 'En curso' : 'Histórico'}
                </p>
              </div>

              <div className="rounded-2xl bg-blue-50 border border-blue-100 px-5 py-3 text-center">
                <div className="text-xs font-semibold text-blue-600 uppercase tracking-wide">
                  Progreso académico
                </div>
                <div className="font-display text-3xl font-extrabold text-blue-700">
                  {summary.academic_progress_percentage ?? 0}%
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-slate-700">Contenido trabajado</span>
                  <span className="text-sm font-bold text-slate-900">
                    {summary.worked_topics ?? 0}/{summary.total_topics ?? 0}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-blue-600 transition-all"
                    style={{ width: `${summary.content_progress_percentage ?? 0}%` }}
                  />
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  Temas trabajados del módulo
                </div>
              </div>

              <div className="rounded-2xl bg-slate-50 border border-slate-100 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-slate-700">Evaluaciones aprobadas</span>
                  <span className="text-sm font-bold text-slate-900">
                    {summary.approved_evaluable_topics ?? 0}/{summary.evaluable_topics ?? 0}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-emerald-500 transition-all"
                    style={{ width: `${summary.academic_progress_percentage ?? 0}%` }}
                  />
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  Temas evaluables aprobados
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {academicProgress?.enrollments?.length > 0 && (
        <div className="rounded-3xl bg-white border border-slate-200 p-6 mb-6">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-700 grid place-items-center">
              <Trophy className="w-5 h-5" />
            </div>
            <div>
              <h2 className="font-display font-extrabold text-xl text-slate-900">
                Historial académico
              </h2>
              <p className="text-sm text-slate-500">
                Tu recorrido por los módulos de Chess4Kings
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {academicProgress.enrollments.map((item, index) => {
              const enrollment = item.enrollment || {};
              const summary = item.summary || {};
              const moduleSlot = enrollment.module_slot;

              const moduleLevel = moduleSlot
                ? moduleSlot <= 4
                  ? 'Básico'
                  : moduleSlot <= 8
                    ? 'Intermedio'
                    : 'Avanzado'
                : '—';

              const statusConfig = {
                passed: {
                  label: 'Aprobado',
                  icon: '✓',
                  className: 'bg-emerald-50 text-emerald-700 border-emerald-200'
                },
                in_progress: {
                  label: 'En curso',
                  icon: '●',
                  className: 'bg-blue-50 text-blue-700 border-blue-200'
                },
                failed: {
                  label: 'No aprobado',
                  icon: '!',
                  className: 'bg-rose-50 text-rose-700 border-rose-200'
                },
                not_started: {
                  label: 'Pendiente',
                  icon: '○',
                  className: 'bg-slate-50 text-slate-600 border-slate-200'
                }
              };

              const status = statusConfig[enrollment.status] || statusConfig.not_started;

              const typeLabel = enrollment.type === 'reinforcement'
                ? 'Reforzamiento'
                : enrollment.type === 'initial'
                  ? 'Ingreso'
                  : null;

              return (
                <div
                  key={enrollment.id || index}
                  className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    <div className="flex items-center gap-3 min-w-0 lg:flex-1">
                      <div className={`w-10 h-10 rounded-full border grid place-items-center font-bold ${status.className}`}>
                        {status.icon}
                      </div>

                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-display font-bold text-lg text-slate-900">
                            Módulo {moduleSlot}
                          </span>
                          <span className="text-sm text-slate-500">
                            · {moduleLevel}
                          </span>
                          {typeLabel && (
                            <span className="text-xs font-semibold px-2 py-1 rounded-full bg-white border border-slate-200 text-slate-600">
                              {typeLabel}
                            </span>
                          )}
                        </div>

                        <div className="text-sm text-slate-500 mt-1">
                          Iniciado {enrollment.started_at
                            ? new Date(enrollment.started_at).toLocaleDateString()
                            : '—'}
                        </div>
                      </div>
                    </div>

                    <div className={`self-start lg:self-auto px-3 py-1.5 rounded-full border text-xs font-bold ${status.className}`}>
                      {status.label}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4">
                    <div className="rounded-xl bg-white border border-slate-200 px-3 py-2">
                      <div className="text-xs text-slate-500">Contenido</div>
                      <div className="font-bold text-slate-900 mt-1">
                        {summary.worked_topics ?? 0}/{summary.total_topics ?? 0}
                      </div>
                    </div>

                    <div className="rounded-xl bg-white border border-slate-200 px-3 py-2">
                      <div className="text-xs text-slate-500">Evaluación</div>
                      <div className="font-bold text-slate-900 mt-1">
                        {summary.approved_evaluable_topics ?? 0}/{summary.evaluable_topics ?? 0}
                      </div>
                    </div>

                    <div className="rounded-xl bg-white border border-slate-200 px-3 py-2 col-span-2 md:col-span-1">
                      <div className="text-xs text-slate-500">Progreso académico</div>
                      <div className="font-bold text-blue-700 mt-1">
                        {summary.academic_progress_percentage ?? 0}%
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="rounded-2xl bg-white border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-xl bg-blue-100 text-blue-700 grid place-items-center"><UserIcon className="w-4 h-4"/></div>
            <h2 className="font-display font-bold text-xl">Cambiar nombre</h2>
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="fn">Nombre</Label>
              <Input id="fn" data-testid="profile-first-name" value={firstName} onChange={(e)=>setFirstName(e.target.value)}/>
            </div>
            <div>
              <Label htmlFor="ln">Apellido</Label>
              <Input id="ln" data-testid="profile-last-name" value={lastName} onChange={(e)=>setLastName(e.target.value)}/>
            </div>
            <Button data-testid="profile-save-name-btn" onClick={saveName} disabled={savingName}
              className="rounded-full bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-1"/> {savingName ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-9 h-9 rounded-xl bg-rose-100 text-rose-700 grid place-items-center"><KeyRound className="w-4 h-4"/></div>
            <h2 className="font-display font-bold text-xl">Cambiar contraseña</h2>
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="cp">Contraseña actual</Label>
              <Input id="cp" data-testid="profile-current-pwd" type="password" value={pwd.current}
                onChange={(e)=>setPwd({...pwd, current: e.target.value})}/>
            </div>
            <div>
              <Label htmlFor="np">Nueva contraseña</Label>
              <Input id="np" data-testid="profile-new-pwd" type="password" value={pwd.next}
                onChange={(e)=>setPwd({...pwd, next: e.target.value})}/>
            </div>
            <div>
              <Label htmlFor="cf">Confirmar</Label>
              <Input id="cf" data-testid="profile-confirm-pwd" type="password" value={pwd.confirm}
                onChange={(e)=>setPwd({...pwd, confirm: e.target.value})}/>
            </div>
            <Button data-testid="profile-save-pwd-btn" onClick={savePwd} disabled={savingPwd}
              className="rounded-full bg-rose-600 hover:bg-rose-700">
              <KeyRound className="w-4 h-4 mr-1"/> {savingPwd ? 'Guardando…' : 'Actualizar contraseña'}
            </Button>
            <p className="text-xs text-slate-500">Al cambiarla, se cerrarán tus sesiones en otros dispositivos.</p>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-6">
        <h2 className="font-display font-bold text-xl mb-4">Historial de puntos</h2>
        {history.length === 0 ? (
          <div className="text-sm text-slate-500">Aún no tienes movimientos.</div>
        ) : (
          <div className="divide-y">
            {history.map(h => (
              <div key={h.id} className="py-3 flex items-center justify-between text-sm">
                <div>
                  <div className="font-semibold">{h.reason.replace('_',' ')}</div>
                  <div className="text-xs text-slate-500">{new Date(h.created_at).toLocaleString()}</div>
                </div>
                <div className="font-mono font-bold text-emerald-600">+{h.amount}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
